import { default as React, useCallback, useEffect, useState } from 'react';
import { Entity, RELATION_PART_OF } from '@backstage/catalog-model';
import {
  EntityFilter,
  useEntityList,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { Select } from '@backstage/core-components';
import Box from '@material-ui/core/Box';

/**
 * EntityFilter that keeps only components whose RELATION_PART_OF
 * matches one of the selected system entity refs.
 *
 * Note: SolutionPicker always passes exactly one system ref when active,
 * so `selectedSystems` is never empty when this filter is in use.
 * An empty array with `active=true` means no match — returns false.
 */
export class SolutionEntityFilter implements EntityFilter {
  constructor(
    readonly selectedSystems: string[],
    readonly active: boolean = true,
  ) {}

  filterEntity(entity: Entity): boolean {
    if (!this.active) return true;
    if (this.selectedSystems.length === 0) return false;

    const systemRefs =
      entity.relations
        ?.filter(
          r => r.type === RELATION_PART_OF && r.targetRef.startsWith('system:'),
        )
        .map(r => r.targetRef) ?? [];

    return systemRefs.some(ref => this.selectedSystems.includes(ref));
  }
}

type SystemEntry = {
  label: string;
  value: string; // entity ref, e.g. "system:default/my-system"
};

/**
 * Dropdown filter for the catalog page that lets users filter components
 * by the System (Solution) they belong to via RELATION_PART_OF.
 */
export function SolutionPicker() {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const { updateFilters } = useEntityList();
  const [systems, setSystems] = useState<SystemEntry[]>([]);
  const [selected, setSelected] = useState<string>('all');

  // Fetch all System entities on mount
  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'System' },
        fields: ['metadata.name', 'metadata.namespace', 'metadata.title'],
      })
      .then(response => {
        const entries: SystemEntry[] = response.items
          .map(e => ({
            label: e.metadata.title || e.metadata.name,
            value: `system:${e.metadata.namespace || 'default'}/${
              e.metadata.name
            }`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setSystems(entries);
      })
      .catch(() => {
        alertApi.post({
          message: 'Failed to load solution filter options.',
          severity: 'error',
        });
      });
  }, [catalogApi, alertApi]);

  const handleChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const stringValue = String(value);
      setSelected(stringValue);
      updateFilters({
        solution:
          stringValue === 'all'
            ? undefined
            : new SolutionEntityFilter([stringValue], true),
      } as Record<string, EntityFilter | undefined>);
    },
    [updateFilters],
  );

  const items = [
    { value: 'all', label: 'All' },
    ...systems.map(s => ({ value: s.value, label: s.label })),
  ];

  return (
    <Box pb={1} pt={1}>
      <Select
        label="Solution"
        items={items}
        selected={selected}
        onChange={handleChange}
      />
    </Box>
  );
}
